import React, { useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote } from 'lucide-react';
import {
  motion,
  useInView,
  FadeInWhenVisible,
  MagneticButton
} from './animations';

const testimonials = [
  {
    name: 'Maria Gonzalez',
    treatment: 'Tratamiento Facial',
    rating: 5,
    comment: 'Increible experiencia. El personal es muy profesional y los resultados son visibles desde la primera sesion. Mi piel luce radiante.',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80'
  },
  {
    name: 'Laura Martinez',
    treatment: 'Medicina Estetica',
    rating: 5,
    comment: 'Las mejores profesionales de Valencia. Me senti comoda y segura durante todo el proceso. Los resultados son naturales y hermosos.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80'
  },
  {
    name: 'Carmen Ruiz',
    treatment: 'Tratamiento Corporal',
    rating: 5,
    comment: 'Despues de 8 sesiones los resultados son evidentes. El trato es excelente y las instalaciones muy limpias. 100% recomendable.',
    image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=200&q=80'
  }
];

const TestimonialCard = ({ testimonial, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, rotateY: -10 }}
      animate={isInView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
      transition={{
        duration: 0.7,
        delay: index * 0.2,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <motion.div
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <Card className="group hover:shadow-elegant transition-all duration-500 border-border/50 bg-gradient-card overflow-hidden h-full">
          <CardContent className="p-6">
            {/* Quote Icon with animation */}
            <motion.div
              className="mb-4"
              initial={{ scale: 0, rotate: -45 }}
              animate={isInView ? { scale: 1, rotate: 0 } : {}}
              transition={{
                type: "spring",
                stiffness: 200,
                delay: 0.3 + index * 0.1
              }}
            >
              <motion.div
                whileHover={{ scale: 1.2, rotate: 10 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Quote className="w-10 h-10 text-primary/30" />
              </motion.div>
            </motion.div>

            {/* Animated Stars */}
            <div className="flex gap-1 mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    delay: 0.4 + i * 0.08 + index * 0.1
                  }}
                  whileHover={{ scale: 1.3, rotate: 20 }}
                >
                  <Star className="w-5 h-5 fill-accent text-accent" />
                </motion.div>
              ))}
            </div>

            {/* Comment with fade-in */}
            <motion.p
              className="text-sm text-muted-foreground leading-relaxed mb-6 italic"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            >
              "{testimonial.comment}"
            </motion.p>

            {/* Author Info with slide animation */}
            <motion.div
              className="flex items-center gap-3 pt-4 border-t border-border/50"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
            >
              <motion.div
                className="relative"
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <motion.img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    delay: 0.5 + index * 0.1
                  }}
                />
                {/* Online indicator */}
                <motion.div
                  className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ delay: 0.8 + index * 0.1, type: "spring" }}
                />
              </motion.div>
              <div>
                <motion.h4
                  className="font-semibold text-foreground"
                  whileHover={{ color: "hsl(var(--primary))" }}
                >
                  {testimonial.name}
                </motion.h4>
                <p className="text-xs text-muted-foreground">{testimonial.treatment}</p>
              </div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

const Testimonials = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true });

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Decorative background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
      >
        <motion.div
          className="absolute top-1/2 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 20, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-48 h-48 bg-secondary/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            y: [0, -20, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="container mx-auto relative">
        {/* Section Header */}
        <FadeInWhenVisible direction="up" className="text-center mb-16">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Lo que dicen{' '}
            <motion.span
              className="text-gradient-primary inline-block"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              nuestras clientas
            </motion.span>
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Testimonios reales de clientas satisfechas
          </motion.p>
        </FadeInWhenVisible>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} index={index} />
          ))}
        </div>

        {/* CTA with magnetic button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <motion.p
            className="text-muted-foreground mb-4"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Lista para tu transformacion?
          </motion.p>
          <MagneticButton strength={0.15}>
            <motion.button
              onClick={() => {
                const formSection = document.getElementById('contact-form');
                if (formSection) formSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-gradient-primary text-primary-foreground font-semibold rounded-full hover:shadow-glow transition-all duration-300 relative overflow-hidden group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative z-10">Reserva tu valoracion gratuita</span>
              <motion.div
                className="absolute inset-0 bg-white/20"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.6 }}
              />
            </motion.button>
          </MagneticButton>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          className="flex justify-center gap-8 mt-12 flex-wrap"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
        >
          {['Google 5.0', 'Instagram', 'Certificado'].map((badge, i) => (
            <motion.div
              key={badge}
              className="flex items-center gap-2 text-sm text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 + i * 0.1 }}
              whileHover={{ scale: 1.1, color: "hsl(var(--primary))" }}
            >
              <motion.div
                className="w-2 h-2 rounded-full bg-green-400"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
              />
              {badge}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
